import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SocialLinks } from "@/components/SocialLinks";
import { SiteHeader } from "@/components/SiteHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: "long" }) : "");

const fade = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

export default function Blog() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    axios.get(`${API}/blog`).then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="blog-page">
      <SiteHeader />

      <main className="max-w-4xl mx-auto px-6 py-16 lg:py-24 w-full flex-1">
        <motion.div {...fade}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]">Insights</p>
          <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-black mt-4">
            The Blog
          </h1>
          <p className="text-base md:text-lg text-[#3A4A63] leading-relaxed mt-6 max-w-2xl">
            Career strategy, growth, and honest guidance from Rhea Sharma.
          </p>
        </motion.div>

        <div className="mt-14 space-y-10">
          {posts === null && <p className="text-sm text-[#9CA3AF]" data-testid="blog-loading">Loading posts...</p>}
          {posts && posts.length === 0 && (
            <p className="text-sm text-[#9CA3AF]" data-testid="blog-empty">No posts yet. Check back soon.</p>
          )}
          {posts && posts.map((p, i) => (
            <motion.article
              key={p.id}
              {...fade}
              transition={{ ...fade.transition, delay: Math.min(i * 0.06, 0.3) }}
              data-testid={`blog-card-${p.slug}`}
              className="border-b border-[#E5E7EB] pb-10 group"
            >
              <p className="text-xs uppercase tracking-[0.15em] text-[#9CA3AF]">{fmt(p.created_at)}</p>
              <Link to={`/blog/${p.slug}`} data-testid={`blog-card-link-${p.slug}`}>
                <h2 className="font-head text-2xl md:text-3xl font-bold mt-2 group-hover:text-[#D94833] transition-colors">
                  {p.title}
                </h2>
              </Link>
              <p className="text-base text-[#3A4A63] leading-relaxed mt-3">{p.excerpt}</p>
              <Link to={`/blog/${p.slug}`}
                    className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-[#041C3E] hover:text-[#D94833] transition-colors">
                Read more <ArrowRight size={15} />
              </Link>
            </motion.article>
          ))}
        </div>
      </main>

      <footer className="bg-[#041C3E] text-white py-12">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row justify-between gap-6 items-start md:items-center">
          <p className="font-head font-black text-xl">The Next Move<span className="text-[#D94833]"> by Rhellex</span></p>
          <div data-testid="footer-contact" className="md:text-center">
            <p className="text-white text-sm font-semibold">Contact Rhea</p>
            <a href="mailto:thenextmovebyrhellex@gmail.com" className="text-white/60 text-sm hover:text-white transition-colors">thenextmovebyrhellex@gmail.com</a>
          </div>
          <SocialLinks tone="dark" />
        </div>
      </footer>
    </div>
  );
}
